import { Injectable, inject } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Observable, switchMap } from 'rxjs';

export interface AlertProperties {
  headerKey?: string;
  messageKey: string;
  subHeaderKey?: string;
  cancelButtonKey?: string;
  cancelButtonCssClass?: string;
  okButtonKey?: string;
  okButtonCssClass?: string;
  interpolateParams?: object;
  hasCancelButton?: boolean;
  backdropDismiss?: boolean;
  cssClass?: string;
}

export interface AlertConfirmProperties {
  messageKey: string;
  okButtonKey: string;
  cancelButtonKey: string;
  interpolateParams?: object;
  isDanger?: boolean;
  okButtonCssClass?: string;
  cancelButtonCssClass?: string;
}

export interface AlertConfirmProperties {
  messageKey: string;
  okButtonKey: string;
  cancelButtonKey: string;
  interpolateParams?: object;
  isDanger?: boolean;
}
@Injectable({
  providedIn: 'root',
})
export class AlertService {
  private readonly alertController = inject(AlertController);
  private readonly translateService = inject(TranslateService);

  /**
   * Show an alert with a header, message and optional subheader
   * @param headerKey
   * @param messageKey
   * @param subHeaderKey
   * @returns {Observable<boolean>} returns false when dismissed, true when clicking continue
   */
  show(props: AlertProperties): Observable<boolean> {
    const defaultCancelButtonKey = 'general_cancel';
    const defaultOkButtonKey = 'general_ok';
    const cancelButtonKey = props.cancelButtonKey ?? defaultCancelButtonKey;
    const okButtonKey = props.okButtonKey ?? defaultOkButtonKey;
    const translationKeys = [props.messageKey, cancelButtonKey, okButtonKey];
    if (props.headerKey) translationKeys.push(props.headerKey);
    if (props.subHeaderKey) translationKeys.push(props.subHeaderKey);
    return this.translateService
      .stream(translationKeys, props.interpolateParams)
      .pipe(
        switchMap(
          (translations: { [key: string]: string }) =>
            new Observable<boolean>((observer) => {
              this.alertController
                .create({
                  header: props.headerKey
                    ? translations[props.headerKey]
                    : undefined,
                  subHeader: props.subHeaderKey
                    ? translations[props.subHeaderKey]
                    : undefined,
                  message: translations[props.messageKey],
                  backdropDismiss:
                    props.backdropDismiss === undefined
                      ? true
                      : props.backdropDismiss,
                  cssClass: props.cssClass,
                  buttons: [
                    props.hasCancelButton
                      ? {
                          text: translations[cancelButtonKey],
                          role: 'cancel',
                          cssClass: props.cancelButtonCssClass,
                          handler: () => observer.next(false),
                        }
                      : '',
                    {
                      text: translations[okButtonKey],
                      cssClass: props.okButtonCssClass,
                      handler: () => observer.next(true),
                    },
                  ].filter((button) => button),
                })
                .then((alert: HTMLIonAlertElement) => {
                  alert.present();
                  alert.onWillDismiss().then((options) => {
                    if (options.role === 'backdrop') {
                      observer.next(false);
                    }
                  });
                  return true;
                });
            }),
        ),
      );
  }
  confirm(props: AlertConfirmProperties): Observable<boolean> {
    return this.show({
      messageKey: props.messageKey,
      okButtonKey: props.okButtonKey,
      cancelButtonKey: props.cancelButtonKey,
      hasCancelButton: true,
      interpolateParams: props.interpolateParams,
      cssClass: props.isDanger ? 'confirm-danger' : undefined,
      okButtonCssClass: 'confirm-ok-button',
      cancelButtonCssClass: 'confirm-cancel-button',
    });
  }

  showMessage(messageKey: string): Observable<boolean> {
    return this.show({
      messageKey,
    });
  }
}
